import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Box,
  IconButton,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
  SelectChangeEvent,
  Autocomplete
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { Performance } from '../../types/mongodb';
import { purchaseOrderService } from '../../services/purchaseOrderService';
import { budgetService } from '../../services/budgetService';
import { ExpectedInvoice, PerformanceBudgetItem } from '../../types/PurchaseOrder';
import { IMC_EXPENSE_CODES, IMC_INCOME_CODES } from '../../constants/imcCodes';

interface CreatePOModalProps {
  open: boolean;
  onClose: () => void;
  performance: Performance;
  onSuccess: () => void;
}

export const CreatePOModal: React.FC<CreatePOModalProps> = ({
  open,
  onClose,
  performance,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [budgetItems, setBudgetItems] = useState<PerformanceBudgetItem[]>([]);
  const [selectedBudgetItems, setSelectedBudgetItems] = useState<{ [key: string]: boolean }>({});
  const [expectedInvoices, setExpectedInvoices] = useState<ExpectedInvoice[]>([]);
  const [notes, setNotes] = useState<string>('');
  const [emailToMonday, setEmailToMonday] = useState<boolean>(true);
  const [bandMemberCount, setBandMemberCount] = useState<number>(3);
  const [invoiceMode, setInvoiceMode] = useState<{[category: string]: 'single' | 'multiple'}>({
    artist: 'single',
    technical: 'single'
  });

  // Format IMC codes for autocomplete
  const imcCodeOptions = [...Object.values(IMC_EXPENSE_CODES), ...Object.values(IMC_INCOME_CODES)].map(code => ({
    value: code.code,
    label: `${code.code} - ${code.label.split(' - ').slice(1).join(' - ')}`,
    description: code.description,
    category: code.category,
    subCategories: code.subCategories
  }));

  // Load budget items for this performance
  useEffect(() => {
    if (open && performance._id) {
      const fetchBudget = async () => {
        try {
          setLoading(true);
          setError(null);
          
          const budget = await budgetService.getBudgetByPerformance(performance._id);
          setBudgetItems(budget.items);
          
          // Initialize selected items (exclude contingency by default)
          const initialSelection = budget.items.reduce((acc, item) => {
            acc[item._id!] = !item.isContingency;
            return acc;
          }, {} as { [key: string]: boolean });
          
          setSelectedBudgetItems(initialSelection);
          
          // Initialize expected invoices based on non-contingency budget items
          initializeExpectedInvoices(budget.items);
          
        } catch (err) {
          console.error('Error fetching budget:', err);
          setError('Failed to load budget data');
        } finally {
          setLoading(false);
        }
      };
      
      fetchBudget();
    }
  }, [open, performance._id]);

  const initializeExpectedInvoices = (items: PerformanceBudgetItem[]) => {
    const initialInvoices: ExpectedInvoice[] = [];
    
    // Artist fee - default to single invoice
    const artistFeeItem = items.find(item => item.category === 'artist');
    if (artistFeeItem) {
      if (invoiceMode.artist === 'single') {
        initialInvoices.push({
          category: 'artist',
          payee: performance.simpleBandName || 
            (typeof performance.band === 'object' ? performance.band?.artistName : 'Artist'),
          description: 'Performance fee',
          amount: artistFeeItem.budgetedAmount,
          status: 'pending',
          imcCode: '114' // Default to "114 - Artists Fees - Domestic Jazz" for artist fees
        });
      } else {
        // Handle multiple band members case using the bandMemberCount state
        const bandName = performance.simpleBandName || 
          (typeof performance.band === 'object' ? performance.band?.artistName : 'Artist');
          
        // Use the bandMemberCount from state
        const totalMembers = bandMemberCount;
        
        if (totalMembers > 0) {
          const memberFee = artistFeeItem.budgetedAmount / totalMembers;
          
          for (let i = 0; i < totalMembers; i++) {
            initialInvoices.push({
              category: 'artist',
              payee: i === 0 ? `${bandName} (Leader)` : `Band Member ${i+1}`,
              description: 'Performance fee',
              amount: memberFee,
              status: 'pending',
              imcCode: '114' // Default to "114 - Artists Fees - Domestic Jazz" for artist fees
            });
          }
        }
      }
    }
      
    // Technical fees
    const technicalItems = items.filter(item => item.category === 'technical');
    if (technicalItems.length > 0) {
      if (invoiceMode.technical === 'single') {
        initialInvoices.push({
          category: 'technical',
          payee: 'Sound Engineer',
          description: 'Technical services',
          amount: technicalItems.reduce((sum, item) => sum + item.budgetedAmount, 0),
          status: 'pending',
          imcCode: '131' // Default to "131 - Production - PA Engineers" for technical fees
        });
      } else {
        // Add individual technical invoices
        technicalItems.forEach(item => {
          let defaultCode = '';
          // Intelligent default codes based on description
          if (item.description?.toLowerCase().includes('sound') || item.description?.toLowerCase().includes('pa')) {
            defaultCode = '131'; // PA Engineers
          } else if (item.description?.toLowerCase().includes('light')) {
            defaultCode = '134'; // Lighting Engineers
          } else {
            defaultCode = '126'; // Other Crew
          }
          
          initialInvoices.push({
            category: 'technical',
            payee: item.description || 'Technical Service',
            description: item.description || '',
            amount: item.budgetedAmount,
            status: 'pending',
            imcCode: defaultCode
          });
        });
      }
    }
    
    // Other non-contingency items
    items.forEach(item => {
      if (!item.isContingency && 
          item.category !== 'artist' && 
          item.category !== 'technical') {
        
        // Assign appropriate default IMC code based on category
        let defaultCode = '';
        switch (item.category) {
          case 'venue':
            defaultCode = '143'; // Venue Hire
            break;
          case 'accommodation':
            defaultCode = '118'; // Artists - Accommodation
            break;
          case 'travel':
            defaultCode = '117'; // Artists - Local Transport
            break;
          case 'catering':
            defaultCode = '119'; // Artists - Catering & Hospitality
            break;
          case 'equipment':
            defaultCode = '132'; // Production - Equipment Hire
            break;
          default:
            defaultCode = '141'; // Sundry Production Expenses
            break;
        }
        
        initialInvoices.push({
          category: item.category,
          payee: item.description || item.category,
          description: item.description || '',
          amount: item.budgetedAmount,
          status: 'pending',
          imcCode: defaultCode
        });
      }
    });
    
    setExpectedInvoices(initialInvoices);
  };

  const handleInvoiceModeChange = (category: string, mode: 'single' | 'multiple') => {
    setInvoiceMode(prev => ({
      ...prev,
      [category]: mode
    }));
    
    // Reinitialize expected invoices with the new mode
    initializeExpectedInvoices(budgetItems);
  };

  const handleBudgetItemToggle = (itemId: string) => {
    setSelectedBudgetItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
    
    // TODO: Update expected invoices based on selected budget items
  };

  const handleInvoiceChange = (index: number, field: keyof ExpectedInvoice, value: any) => {
    setExpectedInvoices(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value
      };
      return updated;
    });
  };

  const handleAddInvoice = (category: string) => {
    setExpectedInvoices(prev => [
      ...prev,
      {
        category: category as 'artist' | 'technical' | 'venue' | 'transport' | 'other',
        payee: '',
        amount: 0,
        status: 'pending',
        imcCode: '',
        description: ''
      }
    ]);
  };

  const handleRemoveInvoice = (index: number) => {
    setExpectedInvoices(prev => prev.filter((_, i) => i !== index));
  };

  const calculateTotal = () => {
    return expectedInvoices.reduce((sum, invoice) => sum + invoice.amount, 0);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Validate form
      if (expectedInvoices.length === 0) {
        setError('At least one expected invoice is required');
        return;
      }
      
      for (const invoice of expectedInvoices) {
        if (!invoice.payee) {
          setError('All payees must be specified');
          return;
        }
        if (invoice.amount <= 0) {
          setError('All invoice amounts must be greater than zero');
          return;
        }
        if (!invoice.imcCode) {
          setError('All invoices must have an IMC code assigned');
          return;
        }
      }
      
      // Get selected budget categories
      const selectedCategories = Object.entries(selectedBudgetItems)
        .filter(([_, selected]) => selected)
        .map(([id, _]) => {
          const item = budgetItems.find(item => item._id === id);
          return item ? item.category : '';
        })
        .filter(Boolean);
      
      // Create PO
      await purchaseOrderService.createPurchaseOrder({
        performanceId: performance._id,
        expectedInvoices,
        budgetCategories: selectedCategories,
        notes,
        emailToMonday
      });
      
      onSuccess();
      onClose();
      
    } catch (err: any) {
      console.error('Error creating purchase order:', err);
      setError(err.message || 'Failed to create purchase order');
    } finally {
      setLoading(false);
    }
  };

  // Helper to find IMC code label by code value
  const getIMCCodeLabel = (code: string) => {
    const foundCode = [...Object.values(IMC_EXPENSE_CODES), ...Object.values(IMC_INCOME_CODES)]
      .find(item => item.code === code);
    
    return foundCode ? `${foundCode.code} - ${foundCode.label.split(' - ').slice(1).join(' - ')}` : '';
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Create Purchase Order</DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {loading && !budgetItems.length ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Performance Details
              </Typography>
              <Typography>
                <strong>Artist:</strong> {performance.simpleBandName || 
                  (typeof performance.band === 'object' ? performance.band?.artistName : 'Unknown')}
              </Typography>
              <Typography>
                <strong>Date:</strong> {performance.startTime ? new Date(performance.startTime).toLocaleDateString() : 'TBD'}
              </Typography>
              <Typography>
                <strong>Venue:</strong> {
                  performance.event && 
                  typeof performance.event === 'object' && 
                  performance.event.venue && 
                  typeof performance.event.venue === 'object' ? 
                    performance.event.venue.name : 'Unknown'
                }
              </Typography>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Budget Items
              </Typography>
              
              {budgetItems.length === 0 ? (
                <Alert severity="warning">
                  No budget found for this performance. Please create a budget first.
                </Alert>
              ) : (
                <List>
                  {budgetItems.map((item) => (
                    <ListItem key={item._id}>
                      <Checkbox
                        checked={selectedBudgetItems[item._id!] || false}
                        onChange={() => handleBudgetItemToggle(item._id!)}
                        disabled={item.isContingency}
                      />
                      <ListItemText
                        primary={`${item.category}: ${item.description || 'No description'}`}
                        secondary={`€${item.budgetedAmount.toFixed(2)}${item.isContingency ? ' (Contingency)' : ''}`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Configure Expected Invoices
              </Typography>
              
              {budgetItems.some(item => item.category === 'artist') && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Artist Fee Invoicing:
                  </Typography>
                  <FormControl component="fieldset">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={invoiceMode.artist === 'single'}
                          onChange={() => handleInvoiceModeChange('artist', 'single')}
                        />
                      }
                      label="Single invoice from artist/band"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={invoiceMode.artist === 'multiple'}
                          onChange={() => handleInvoiceModeChange('artist', 'multiple')}
                        />
                      }
                      label="Individual invoices from band members"
                    />
                  </FormControl>
                  
                  {invoiceMode.artist === 'multiple' && (
                    <Box sx={{ mt: 2, ml: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <TextField
                        label="Number of band members"
                        type="number"
                        value={bandMemberCount}
                        onChange={(e) => {
                          const count = parseInt(e.target.value) || 1;
                          setBandMemberCount(count);
                          // Update this after changing the count
                          setTimeout(() => initializeExpectedInvoices(budgetItems), 0);
                        }}
                        inputProps={{ min: 1 }}
                        size="small"
                        sx={{ width: 180 }}
                      />
                      <Button 
                        variant="outlined" 
                        size="small"
                        onClick={() => {
                          initializeExpectedInvoices(budgetItems);
                        }}
                      >
                        Update Invoices
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
              
              {/* Technical Invoicing Mode */}
              {budgetItems.some(item => item.category === 'technical') && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Technical Fee Invoicing:
                  </Typography>
                  <FormControl component="fieldset">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={invoiceMode.technical === 'single'}
                          onChange={() => handleInvoiceModeChange('technical', 'single')}
                        />
                      }
                      label="Single invoice for all technical services"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={invoiceMode.technical === 'multiple'}
                          onChange={() => handleInvoiceModeChange('technical', 'multiple')}
                        />
                      }
                      label="Separate invoices for different technical services"
                    />
                  </FormControl>
                </Box>
              )}
              
              <Typography variant="subtitle1" gutterBottom>
                Expected Invoices:
              </Typography>
              
              {expectedInvoices.map((invoice, index) => (
                <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #ccc', borderRadius: 1 }}>
                  <Grid container spacing={2} alignItems="flex-start">
                    {/* Row 1: Category, Payee, Amount and Remove Button */}
                    <Grid item xs={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Category</InputLabel>
                        <Select
                          value={invoice.category}
                          onChange={(e: SelectChangeEvent<string>) => 
                            handleInvoiceChange(index, 'category', e.target.value as any)}
                          label="Category"
                        >
                          <MenuItem value="artist">Artist</MenuItem>
                          <MenuItem value="technical">Technical</MenuItem>
                          <MenuItem value="venue">Venue</MenuItem>
                          <MenuItem value="transport">Transport</MenuItem>
                          <MenuItem value="other">Other</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Payee"
                        value={invoice.payee}
                        onChange={(e) => handleInvoiceChange(index, 'payee', e.target.value)}
                      />
                    </Grid>
                    
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Amount (€)"
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        value={invoice.amount}
                        onChange={(e) => handleInvoiceChange(index, 'amount', parseFloat(e.target.value) || 0)}
                      />
                    </Grid>
                    
                    <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <IconButton 
                        color="error" 
                        onClick={() => handleRemoveInvoice(index)}
                        size="small"
                      >
                        <RemoveIcon />
                      </IconButton>
                    </Grid>
                    
                    {/* Row 2: Description and IMC Code */}
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Description"
                        value={invoice.description || ''}
                        onChange={(e) => handleInvoiceChange(index, 'description', e.target.value)}
                      />
                    </Grid>
                    
                    <Grid item xs={6}>
                      <Autocomplete
                        size="small"
                        options={imcCodeOptions}
                        getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                        value={invoice.imcCode ? 
                          imcCodeOptions.find(opt => opt.value === invoice.imcCode) || null : 
                          null
                        }
                        onChange={(_, newValue) => {
                          handleInvoiceChange(
                            index, 
                            'imcCode', 
                            newValue ? newValue.value : ''
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="IMC Code"
                            placeholder="Select IMC code"
                            fullWidth
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props}>
                            <div>
                              <Typography variant="body2">{option.label}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.description}
                              </Typography>
                            </div>
                          </li>
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))}
              
              <Box sx={{ mt: 2, mb: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => handleAddInvoice('other')}
                >
                  Add Invoice
                </Button>
              </Box>
              
              <Typography variant="subtitle1">
                Total PO Amount: €{calculateTotal().toFixed(2)}
              </Typography>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Additional Information
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    multiline
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={emailToMonday}
                        onChange={(e) => setEmailToMonday(e.target.checked)}
                      />
                    }
                    label="Email to Monday.com after creation"
                  />
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || expectedInvoices.length === 0}
        >
          {loading ? <CircularProgress size={24} /> : 'Create Purchase Order'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePOModal;